import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MinusOutlined, PlusOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { Alert, Button, Badge, Col, Divider, Form, List, message, Row, Skeleton } from 'antd';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdOpenInNew } from 'react-icons/md';

import { useGetProductDetails } from 'apis/product';

import { withContextCart } from 'contexts/ContextCart/ContextCart';
import FormInput from 'components/Input/FormInput/FormInput';
import OutOfStockLabel from 'components/_local/OutOfStockLabel/OutOfStockLabel';

import ImagePlaceholder from 'images/no-image-placeholder.jpg';

import { useLocationQuery } from 'hooks/router';
import { getHomeRoute, getCartRoute, useNav } from 'utils/routes';
import { generateQtyLimit } from 'utils/cart';
import { encodeImageUrl, guard } from 'utils/general';

import {
  CartIcon,
  CompactFooterButton,
  CompactFooterControls,
  CompactFooterHint,
  CompactFooterLabel,
  CompactFooterQtyWrapper,
  CompactFooterTopRow,
  CompactHeaderImageFrame,
  CompactHeaderInfo,
  CompactHeaderKeyword,
  CompactHeaderRow,
  CompactHeaderTitle,
  CompactHeaderVarianceLabel,
  CompactModalSectionDesc,
  CompactModalSectionTitle,
  CompactProductDesc,
  ContainerModal,
  OriPricingInfoLabel,
  PhotoCol,
  PricingInfoLabel,
  ProductCategoryTag,
  ProductDesc,
  ProductDetailsPreviewImg,
  ProductKeyword,
  ProductModalImageFrame,
  ProductName,
  ProductNameWithVariances,
  ProductTagsContainer,
  QtyButton,
  QtyButtonGroupContainer,
  QtyStepperControlsRow,
  VarianceCard,
  VarianceCardImage,
  VarianceCardText,
  VarianceCardTitle,
  VarianceCardsContainer
} from './ProductDetailsModal.styles';

const StyledImagesPreview = props => (
  <ProductModalImageFrame>
    <ProductDetailsPreviewImg {...props} />
  </ProductModalImageFrame>
);

const CompactHeaderImage = props => (
  <CompactHeaderImageFrame>
    <ProductDetailsPreviewImg {...props} />
  </CompactHeaderImageFrame>
);

const homeRoute = getHomeRoute();
const cartRoute = getCartRoute();

const { useForm } = Form;
const {
  Item: ListItem,
  Item: { Meta: ListItemMeta }
} = List;

const getCoverPhotoUrl = product => product?.coverPhoto?.original?.url || product?.coverPhotoFile?.url;

const getGalleryPhotoUrls = product => {
  const fromPhotos = product?.photos?.map(photo => photo?.original?.url).filter(Boolean) ?? [];
  if (fromPhotos.length > 0) {
    return fromPhotos;
  }
  const fromFiles = product?.photoFiles?.map(file => file?.url).filter(Boolean) ?? [];
  return fromFiles.length > 0 ? fromFiles : [];
};

const resolveVarianceCoverUrl = (variance, parentProduct) => getCoverPhotoUrl(variance) || getCoverPhotoUrl(parentProduct);

const resolveVarianceGalleryUrls = (variance, parentProduct) => {
  const varianceUrls = getGalleryPhotoUrls(variance);
  if (varianceUrls.length > 0) {
    return varianceUrls;
  }
  return getGalleryPhotoUrls(parentProduct);
};

const ProductCategoryTags = ({ categories, compact }) => {
  const resolved = categories?.filter(tag => tag?.label != null && String(tag.label).trim() !== '') ?? [];
  if (!resolved.length) {
    return null;
  }
  return (
    <ProductTagsContainer $compact={compact}>
      {resolved.map((tag, i) => (
        <ProductCategoryTag bordered={false} $compact={compact} key={tag.storeMsCategoryId ?? `ms-category-${i}`}>
          #{tag.label}
        </ProductCategoryTag>
      ))}
    </ProductTagsContainer>
  );
};

const ProductInfo = ({ t, name, keyword, keywordComment, desc, tags, isVariance = false, isLiveSellingProduct = false }) => {
  return (
    <>
      {!isVariance ? <ProductName>{name}</ProductName> : <ProductNameWithVariances>{name}</ProductNameWithVariances>}
      {keyword && <ProductKeyword>{keyword}</ProductKeyword>}
      {desc && <ProductDesc>{desc}</ProductDesc>}
      <ProductCategoryTags categories={tags} />
      {isLiveSellingProduct && keywordComment && (
        <Alert
          showIcon
          message={
            <span>
              <span>{t('modalProductDetails:comment-fb-alert-message')}</span> <b>{keywordComment} + 1</b>
            </span>
          }
        />
      )}
    </>
  );
};

const QuantityControls = ({
  form,
  formInputName,
  limitQty = 0,
  hasStock,
  addedQty = 0,
  isLiveSellingProduct = false,
  onChangeQtyByBtn,
  showOutOfStockLabel = true,
  isCompactFooter = false
}) => {
  const { t } = useTranslation(['modalProductDetails']);
  const handleOnClickAddQty = () => {
    const formValue = form.getFieldsValue([formInputName]);
    const currentValue = formValue[formInputName];
    const newValue = !currentValue ? 1 : Number(currentValue) + 1;

    form.setFieldsValue({ [formInputName]: newValue });
    form.validateFields([formInputName]);

    onChangeQtyByBtn(formInputName, newValue);
  };

  const handleOnClickDeductQty = () => {
    const formValue = form.getFieldsValue([formInputName]);
    const currentValue = formValue[formInputName];
    const newValue = currentValue > 1 ? Number(currentValue) - 1 : undefined;

    if (!currentValue) {
      return;
    } else {
      form.setFieldsValue({ [formInputName]: newValue });
      form.validateFields([formInputName]);

      onChangeQtyByBtn(formInputName, newValue);
    }
  };

  if (!hasStock) {
    return showOutOfStockLabel ? <OutOfStockLabel /> : null;
  }

  if (isLiveSellingProduct) {
    return null;
  }

  const qtyControlSize = isCompactFooter ? 'large' : 'middle';

  return (
    <QtyButtonGroupContainer isCompactFooter={isCompactFooter}>
      <QtyStepperControlsRow $compact={isCompactFooter}>
        <QtyButton size={qtyControlSize} isCompactFooter={isCompactFooter} icon={<MinusOutlined />} onClick={handleOnClickDeductQty} />
        <FormInput
          name={formInputName}
          placeholder="e.g.: 2"
          size={qtyControlSize}
          isNumeric
          onChange={() => {
            form.validateFields([formInputName]);
          }}
          extraRules={[
            {
              validator: (_, value) => {
                if (Number(value) + Number(addedQty) > limitQty) {
                  return Promise.reject();
                } else {
                  return Promise.resolve();
                }
              },
              message: t('modalProductDetails:product-error-message-not-enough-stock', { limitQty })
            },
            {
              validator: (_, value) => {
                if (!!value && Number(value) < 1) {
                  return Promise.reject();
                } else {
                  return Promise.resolve();
                }
              },
              message: t('modalProductDetails:product-error-message-invalid-quantity')
            }
          ]}
        />
        <QtyButton size={qtyControlSize} isCompactFooter={isCompactFooter} icon={<PlusOutlined />} onClick={handleOnClickAddQty} />
      </QtyStepperControlsRow>
    </QtyButtonGroupContainer>
  );
};

const PricingAndAddToCart = ({
  form,
  formInputName,
  priceAmt,
  oriPrice,
  limitQty = 0,
  spaceTop = '24px',
  hasStock,
  addedQty = 0,
  isLiveSellingProduct = false,
  onChangeQtyByBtn,
  showQtyControls = true
}) => {
  return (
    <Row justify="space-between" align="middle" style={{ marginTop: spaceTop }}>
      <Col>
        {oriPrice && <OriPricingInfoLabel amount={oriPrice} />}
        <PricingInfoLabel amount={priceAmt} hasOriPrice={!!oriPrice} />
      </Col>
      {showQtyControls && (
        <Col span={24} sm={12} md={10} xl={12}>
          <QuantityControls
            form={form}
            formInputName={formInputName}
            limitQty={limitQty}
            hasStock={hasStock}
            addedQty={addedQty}
            isLiveSellingProduct={isLiveSellingProduct}
            onChangeQtyByBtn={onChangeQtyByBtn}
          />
        </Col>
      )}
    </Row>
  );
};

const MainProductView = ({ form, selectedProduct, existingProductItem, isLiveSellingProduct, onChangeQtyByBtn, isCompact = false }) => {
  const { t } = useTranslation(['modalProductDetails']);

  const productPhotoUrl = getCoverPhotoUrl(selectedProduct);
  const productPhotoUrls = getGalleryPhotoUrls(selectedProduct);

  if (isCompact) {
    return (
      <Row gutter={16} style={{ margin: 0 }}>
        <Col span={24}>
          <CompactHeaderRow>
            <CompactHeaderImage
              thumbnailImageUrl={productPhotoUrl || ImagePlaceholder}
              previewImageUrl={productPhotoUrl || ImagePlaceholder}
              imageUrls={productPhotoUrls}
            />
            <CompactHeaderInfo>
              <CompactHeaderTitle>{selectedProduct.label}</CompactHeaderTitle>
              {selectedProduct.keyword && <CompactHeaderKeyword>{selectedProduct.keyword}</CompactHeaderKeyword>}
              <div>
                {selectedProduct.oriPrice && <OriPricingInfoLabel $compact amount={selectedProduct.oriPrice} />}
                <PricingInfoLabel $compact amount={selectedProduct.msPrice} hasOriPrice={!!selectedProduct.oriPrice} />
              </div>
            </CompactHeaderInfo>
          </CompactHeaderRow>
          <ProductCategoryTags categories={selectedProduct.msCategories} compact />
          {isLiveSellingProduct && selectedProduct.keyword && (
            <Alert
              showIcon
              message={
                <span>
                  <span>{t('modalProductDetails:comment-fb-alert-message')}</span> <b>{selectedProduct.keyword} + 1</b>
                </span>
              }
            />
          )}
          {selectedProduct.description && (
            <>
              <Divider style={{ margin: '12px 0' }} />
              <CompactProductDesc>{selectedProduct.description}</CompactProductDesc>
            </>
          )}
        </Col>
      </Row>
    );
  }

  const hasStock = selectedProduct.inventory > 0;

  return (
    <Row gutter={32} style={{ margin: 0 }}>
      <PhotoCol span={24} md={8}>
        <StyledImagesPreview
          thumbnailImageUrl={productPhotoUrl || ImagePlaceholder}
          previewImageUrl={productPhotoUrl || ImagePlaceholder}
          imageUrls={productPhotoUrls}
        />
      </PhotoCol>
      <Col span={24} md={16}>
        <ProductInfo
          t={t}
          name={selectedProduct.label}
          keyword={selectedProduct.keyword}
          keywordComment={selectedProduct.keyword}
          desc={selectedProduct.description}
          tags={selectedProduct.msCategories}
          isLiveSellingProduct={isLiveSellingProduct}
        />
        <PricingAndAddToCart
          form={form}
          formInputName={selectedProduct._id}
          priceAmt={selectedProduct.msPrice}
          oriPrice={selectedProduct.oriPrice}
          limitQty={generateQtyLimit(selectedProduct.inventory, selectedProduct.purchaseLimit)}
          hasStock={hasStock}
          addedQty={existingProductItem && existingProductItem.quantity}
          isLiveSellingProduct={isLiveSellingProduct}
          onChangeQtyByBtn={onChangeQtyByBtn}
          showQtyControls
        />
      </Col>
    </Row>
  );
};

const ProductWithVariancesView = ({ form, selectedProduct, existingProductItems, isLiveSellingProduct, onChangeQtyByBtn }) => {
  const { t } = useTranslation(['modalProductDetails']);

  return (
    <Row gutter={32} style={{ margin: 0 }}>
      <Col span={24}>
        <ProductInfo
          t={t}
          name={selectedProduct.label}
          keyword={selectedProduct.keyword}
          desc={
            selectedProduct.description ||
            t('modalProductDetails:product-fallback-variance-desc', { variancesLabel: selectedProduct.variances.map(v => v.label).join(', ') })
          }
          tags={selectedProduct.msCategories}
          isLiveSellingProduct={isLiveSellingProduct}
        />
      </Col>
      <Col span={24}>
        <Divider />
        <List
          itemLayout="horizontal"
          dataSource={selectedProduct.variances}
          renderItem={variance => {
            const existingProductItem = existingProductItems.find(existingProductItem => variance._id === existingProductItem.varianceId);

            const variancePhotoUrl = resolveVarianceCoverUrl(variance, selectedProduct);
            const variancePhotoUrls = resolveVarianceGalleryUrls(variance, selectedProduct);

            return (
              <ListItem key={variance._id}>
                <ListItemMeta
                  description={
                    <div style={{ width: '100%' }}>
                      <Row gutter={32} style={{ margin: 0 }}>
                        {variancePhotoUrl && (
                          <PhotoCol span={24} md={6}>
                            <StyledImagesPreview
                              thumbnailImageUrl={variancePhotoUrl}
                              previewImageUrl={variancePhotoUrl}
                              imageUrls={variancePhotoUrls}
                            />
                          </PhotoCol>
                        )}
                        <Col span={24} md={variancePhotoUrl ? 18 : 24}>
                          <ProductInfo
                            t={t}
                            name={variance.label}
                            keywordComment={`${selectedProduct.keyword}${variance.keyword}`}
                            desc={variance.description || selectedProduct.description}
                            isVariance
                            isLiveSellingProduct={isLiveSellingProduct}
                          />
                          <PricingAndAddToCart
                            form={form}
                            formInputName={`${selectedProduct._id}_${variance._id}`}
                            priceAmt={variance.msPrice}
                            oriPrice={variance.oriPrice}
                            limitQty={generateQtyLimit(variance.inventory, variance.purchaseLimit)}
                            hasStock={variance.inventory > 0}
                            addedQty={existingProductItem && existingProductItem.quantity}
                            isLiveSellingProduct={isLiveSellingProduct}
                            onChangeQtyByBtn={onChangeQtyByBtn}
                          />
                        </Col>
                      </Row>
                    </div>
                  }
                />
              </ListItem>
            );
          }}
        />
      </Col>
    </Row>
  );
};

const ProductWithVariancesCompactView = ({ selectedProduct, selectedVariance, onChangeSelectedVariance }) => {
  const { t } = useTranslation(['modalProductDetails']);

  const productPhotoUrl = selectedVariance ? resolveVarianceCoverUrl(selectedVariance, selectedProduct) : getCoverPhotoUrl(selectedProduct);
  const productPhotoUrls = selectedVariance ? resolveVarianceGalleryUrls(selectedVariance, selectedProduct) : getGalleryPhotoUrls(selectedProduct);

  return (
    <Row gutter={24} style={{ margin: 0 }}>
      <Col span={24}>
        <CompactHeaderRow>
          {productPhotoUrl && (
            <CompactHeaderImage thumbnailImageUrl={productPhotoUrl} previewImageUrl={productPhotoUrl} imageUrls={productPhotoUrls} />
          )}
          <CompactHeaderInfo>
            <CompactHeaderTitle>{selectedProduct.label}</CompactHeaderTitle>
            {selectedProduct.keyword && <CompactHeaderKeyword>{selectedProduct.keyword}</CompactHeaderKeyword>}
            {selectedVariance && (
              <>
                <CompactHeaderVarianceLabel>{selectedVariance.label}</CompactHeaderVarianceLabel>
                <div>
                  {selectedVariance.oriPrice && <OriPricingInfoLabel $compact amount={selectedVariance.oriPrice} />}
                  <PricingInfoLabel $compact amount={selectedVariance.msPrice} hasOriPrice={!!selectedVariance.oriPrice} />
                </div>
              </>
            )}
          </CompactHeaderInfo>
        </CompactHeaderRow>
        <ProductCategoryTags categories={selectedProduct.msCategories} compact />
      </Col>
      <Col span={24}>
        <VarianceCardsContainer>
          {selectedProduct.variances.map(variance => {
            const variancePhotoUrl = getCoverPhotoUrl(variance);

            return (
              <VarianceCard
                key={variance._id}
                type="button"
                isSelected={selectedVariance?._id === variance._id}
                disabled={!(variance.inventory > 0)}
                onClick={() => {
                  if (selectedVariance?._id !== variance._id) {
                    onChangeSelectedVariance(variance);
                  }
                }}
              >
                {variancePhotoUrl && <VarianceCardImage src={encodeImageUrl(variancePhotoUrl)} alt={variance.label} />}
                <VarianceCardText>
                  <VarianceCardTitle>{variance.label}</VarianceCardTitle>
                </VarianceCardText>
              </VarianceCard>
            );
          })}
        </VarianceCardsContainer>
      </Col>
      <Col span={24}>
        <Divider style={{ margin: '12px 0' }} />
        <CompactModalSectionTitle>{selectedProduct.label}</CompactModalSectionTitle>
        <CompactModalSectionDesc>
          {selectedProduct.description ||
            t('modalProductDetails:product-fallback-variance-desc', { variancesLabel: selectedProduct.variances.map(v => v.label).join(', ') })}
        </CompactModalSectionDesc>
      </Col>
    </Row>
  );
};

const ProductDetailsModal = ({ propsContextCart }) => {
  const { productItems, addProductItems, isCompactProductView } = propsContextCart;
  const { t } = useTranslation(['modalProductDetails']);
  const [form] = useForm();
  const nav = useNav();
  const { productId } = useParams();
  const query = useLocationQuery();
  const postId = query.post;

  const [isVisible, setIsVisible] = useState(true);
  const [hasInitValue, setHasInitValue] = useState(false);
  const [isAllowAddToCart, setIsAllowAddToCart] = useState(false);
  const [selectedCompactVariance, setSelectedCompactVariance] = useState(null);
  const { isLoading: isProductLoading, data: product } = useGetProductDetails(productId, { ...(postId && { postId }) });

  const isLiveSellingProduct = guard(() => !!product.liveVideoUrl);
  const existingProductItems = useMemo(() => productItems.filter(productItem => productItem.productId === productId), [productItems, productId]);
  const hasVariances = !!(product && product.variances && product.variances.length > 0);
  const shouldRenderCompactFixedCta = isCompactProductView && !isLiveSellingProduct;
  const compactSelectedExistingProductItem = existingProductItems.find(
    existingProductItem => selectedCompactVariance && selectedCompactVariance._id === existingProductItem.varianceId
  );
  const selectedCompactVarianceFieldName =
    product && selectedCompactVariance ? `${product._id}_${selectedCompactVariance._id}` : 'compact-variance-placeholder';

  const handleChangeCompactVariance = newVariance => {
    if (selectedCompactVariance) {
      form.setFieldsValue({ [`${product._id}_${selectedCompactVariance._id}`]: undefined });
    }
    setIsAllowAddToCart(false);
    setSelectedCompactVariance(newVariance);
  };

  const generateProductsToAdd = formValues => {
    const filteredFormValues = Object.keys(formValues).reduce((values, key) => {
      if (!!formValues[key]) {
        values.push({ [key]: Number(formValues[key]) });
        return values;
      }
      return values;
    }, []);
    const productsToAdd = [];
    filteredFormValues.forEach(filteredFormValue => {
      const key = Object.keys(filteredFormValue)[0];
      const ids = key.split('_');
      const hasVariance = ids.length > 1;
      if (hasVariance) {
        const detailsOfProductToAdd = product.variances.find(variance => variance._id === ids[1]);
        const productToAdd = {
          _id: `${productId}_${detailsOfProductToAdd._id}`,
          productId,
          varianceId: detailsOfProductToAdd._id,
          label: `${product.label} - ${detailsOfProductToAdd.label}`,
          priceAmount: detailsOfProductToAdd.msPrice,
          oriPrice: detailsOfProductToAdd.oriPrice,
          coverPhotoUrl: resolveVarianceCoverUrl(detailsOfProductToAdd, product),
          coverPhoto: detailsOfProductToAdd.coverPhoto || product.coverPhoto,
          description: detailsOfProductToAdd.description || product.description,
          inventory: detailsOfProductToAdd.inventory,
          purchaseLimit: detailsOfProductToAdd.purchaseLimit,
          quantity: filteredFormValue[key]
        };
        productsToAdd.push(productToAdd);
      } else {
        const detailsOfProductToAdd = product;
        const productToAdd = {
          _id: `${productId}_${detailsOfProductToAdd._id}`,
          productId,
          label: detailsOfProductToAdd.label,
          priceAmount: detailsOfProductToAdd.msPrice,
          coverPhotoUrl: getCoverPhotoUrl(detailsOfProductToAdd),
          coverPhoto: detailsOfProductToAdd.coverPhoto,
          description: detailsOfProductToAdd.description,
          inventory: detailsOfProductToAdd.inventory,
          purchaseLimit: detailsOfProductToAdd.purchaseLimit,
          quantity: filteredFormValue[key]
        };
        productsToAdd.push(productToAdd);
      }
    });
    return productsToAdd;
  };

  const handleOnClickAddToCart = async e => {
    e.preventDefault();
    try {
      const formValues = await form.validateFields();
      const productsToAdd = generateProductsToAdd(formValues);
      addProductItems(productsToAdd)
        .then(() => {
          form.resetFields();
          setIsAllowAddToCart(false);
          message.success(t('modalProductDetails:add-to-cart-success-message'));
        })
        .catch(ex => {
          message.error(ex.message);
        });
    } catch (error) {
      error && error.errorFields && error.errorFields.forEach(field => message.error(field.errors[0]));
    }
  };

  const handleOnChangeQtyByBtn = (fieldName, newValue) => {
    const allFieldsValue = {
      ...form.getFieldsValue(),
      [fieldName]: newValue
    };
    const foundFormWithValue = Object.values(allFieldsValue).find(formProductQty => !!formProductQty && Number(formProductQty) > 0);
    setIsAllowAddToCart(!!foundFormWithValue);
  };

  useEffect(() => {
    if (!hasInitValue && product && product._id && (!product.variances || product.variances.length === 0)) {
      setHasInitValue(true);
      form.setFieldsValue({ [product._id]: 1 });
      setIsAllowAddToCart(true);
    }
  }, [product, form, hasInitValue]);

  useEffect(() => {
    setSelectedCompactVariance(null);
  }, [productId, isCompactProductView]);

  return (
    <>
      {product && product.label && (
        <Helmet>
          <title>{product.label}</title>
        </Helmet>
      )}
      <Form
        form={form}
        style={{ width: '100%' }}
        onValuesChange={(_, allValues) => {
          const foundFormWithValue = Object.values(allValues).find(formProductQty => !!formProductQty && Number(formProductQty) > 0);
          setIsAllowAddToCart(!!foundFormWithValue);
        }}
      >
        {isVisible && (
          <ContainerModal
            open={isVisible}
            isCompactScrollable={isCompactProductView}
            footer={
              shouldRenderCompactFixedCta ? (
                <CompactFooterControls>
                  <CompactFooterTopRow>
                    <CompactFooterLabel>{t('modalProductDetails:quantity-label')}</CompactFooterLabel>
                    {hasVariances ? (
                      selectedCompactVariance ? (
                        <CompactFooterQtyWrapper>
                          <QuantityControls
                            form={form}
                            formInputName={selectedCompactVarianceFieldName}
                            limitQty={generateQtyLimit(selectedCompactVariance.inventory, selectedCompactVariance.purchaseLimit)}
                            hasStock={selectedCompactVariance.inventory > 0}
                            addedQty={compactSelectedExistingProductItem && compactSelectedExistingProductItem.quantity}
                            isLiveSellingProduct={isLiveSellingProduct}
                            onChangeQtyByBtn={handleOnChangeQtyByBtn}
                            isCompactFooter
                          />
                        </CompactFooterQtyWrapper>
                      ) : (
                        <CompactFooterHint>{t('modalProductDetails:select-variance-hint')}</CompactFooterHint>
                      )
                    ) : (
                      <CompactFooterQtyWrapper>
                        <QuantityControls
                          form={form}
                          formInputName={product._id}
                          limitQty={generateQtyLimit(product.inventory, product.purchaseLimit)}
                          hasStock={product.inventory > 0}
                          addedQty={existingProductItems[0] && existingProductItems[0].quantity}
                          isLiveSellingProduct={isLiveSellingProduct}
                          onChangeQtyByBtn={handleOnChangeQtyByBtn}
                          isCompactFooter
                        />
                      </CompactFooterQtyWrapper>
                    )}
                  </CompactFooterTopRow>
                  <CompactFooterButton
                    type="primary"
                    block
                    icon={<ShoppingCartOutlined />}
                    onClick={handleOnClickAddToCart}
                    disabled={
                      isProductLoading ||
                      (hasVariances
                        ? !selectedCompactVariance || !(selectedCompactVariance.inventory > 0) || !isAllowAddToCart
                        : !(product.inventory > 0) || !isAllowAddToCart)
                    }
                  >
                    {t('modalProductDetails:add-to-cart-button')}
                  </CompactFooterButton>
                </CompactFooterControls>
              ) : (
                <Row gutter={8}>
                  <Col flex="auto">
                    {isLiveSellingProduct ? (
                      <Button
                        type="primary"
                        ghost
                        block
                        onClick={() => {
                          window.open(product.liveVideoUrl, '_blank');
                        }}
                        disabled={isProductLoading || !(product.inventory > 0)}
                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                      >
                        {t('modalProductDetails:buy-from-fb-button')} <MdOpenInNew size="16px" style={{ marginLeft: '4px' }} />
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        ghost
                        block
                        icon={<ShoppingCartOutlined />}
                        onClick={handleOnClickAddToCart}
                        disabled={isProductLoading || !(product.inventory > 0) || !isAllowAddToCart}
                      >
                        {t('modalProductDetails:add-to-cart-button')}
                      </Button>
                    )}
                  </Col>
                  {existingProductItems && existingProductItems.length > 0 && (
                    <Col>
                      <Button
                        type="text"
                        icon={
                          <Badge dot>
                            <CartIcon />
                          </Badge>
                        }
                        onClick={() => nav(cartRoute.path)}
                      />
                    </Col>
                  )}
                </Row>
              )
            }
            onCancel={() => {
              setIsVisible(false);
              nav(homeRoute.path);
            }}
            destroyOnClose
          >
            {isProductLoading ? (
              <Skeleton active />
            ) : product.variances && product.variances.length > 0 ? (
              !isCompactProductView ? (
                <ProductWithVariancesView
                  form={form}
                  selectedProduct={product}
                  existingProductItems={existingProductItems}
                  isLiveSellingProduct={isLiveSellingProduct}
                  onChangeQtyByBtn={handleOnChangeQtyByBtn}
                />
              ) : (
                <ProductWithVariancesCompactView
                  selectedProduct={product}
                  selectedVariance={selectedCompactVariance}
                  onChangeSelectedVariance={handleChangeCompactVariance}
                />
              )
            ) : (
              <MainProductView
                form={form}
                selectedProduct={product}
                existingProductItem={existingProductItems[0]}
                isLiveSellingProduct={isLiveSellingProduct}
                onChangeQtyByBtn={handleOnChangeQtyByBtn}
                isCompact={isCompactProductView}
              />
            )}
          </ContainerModal>
        )}
      </Form>
    </>
  );
};

export default withContextCart(ProductDetailsModal);
